import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import type { Academy } from '@court-sync/shared';
import { Button, Field, FieldError, FieldLabel, Input } from '@/components/ui';
import { ACADEMY_SETTINGS_TOUR_STEP_IDS } from '@/lib/tour-constants';
import { updateAcademyQuery } from '@/queries/academy';
import { AgeGroupsSettingsTab } from './AgeGroupsSettingsTab';

interface AcademyGeneralSettingsTabProps {
  academy: Academy;
  onSaved: (academy: Academy) => void;
}

interface AcademyNameFormValues {
  name: string;
}

export function AcademyGeneralSettingsTab({ academy, onSaved }: AcademyGeneralSettingsTabProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<AcademyNameFormValues>({
    defaultValues: { name: academy.name },
  });

  useEffect(() => {
    reset({ name: academy.name });
  }, [academy.name, reset]);

  const onSubmit = async (values: AcademyNameFormValues) => {
    const trimmed = values.name.trim();
    if (!trimmed || trimmed === academy.name) {
      return;
    }

    try {
      const updated = await updateAcademyQuery(academy.id, { name: trimmed });
      onSaved(updated);
      toast.success('Academy name updated');
    } catch {
      toast.error('Failed to update academy name');
    }
  };

  return (
    <div className="max-w-3xl space-y-8">
      <section
        id={ACADEMY_SETTINGS_TOUR_STEP_IDS.NAME}
        aria-labelledby="academy-name-heading"
        className="space-y-4"
      >
        <div>
          <h2 id="academy-name-heading" className="text-lg font-semibold text-gray-900 dark:text-white">
            Academy
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            The display name for your academy programs and lesson catalogue.
          </p>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Field>
            <FieldLabel htmlFor="academy-name">Academy name</FieldLabel>
            <div className="flex items-center gap-2">
              <Input
                id="academy-name"
                className="min-w-0 flex-1"
                {...register('name', { required: 'Academy name is required' })}
              />
              <Button
                type="submit"
                className="shrink-0"
                isLoading={isSubmitting}
                disabled={!isDirty}
              >
                Save academy name
              </Button>
            </div>
            {errors.name ? <FieldError>{errors.name.message}</FieldError> : null}
          </Field>
        </form>
      </section>

      <div id={ACADEMY_SETTINGS_TOUR_STEP_IDS.AGE_GROUPS}>
        <AgeGroupsSettingsTab academy={academy} />
      </div>
    </div>
  );
}
